import React, {
  FC,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import {
  DateRangePicker,
  H3,
  Input,
  Preloader,
  Separator,
  Toggle,
} from '@smartshell/ui-kit';
import { Editor as ReactEditor } from '@tinymce/tinymce-react/lib/cjs/main/ts/components/Editor';
import dayjs from 'dayjs';
import { Controller } from 'react-hook-form';
import { Editor as TinyEditor } from 'tinymce';

import { LocalizationContext } from 'localization';
import { ETheme, ThemeContext } from 'theme';
import { formatDateForServer } from 'utils';

import s from '../../NewsManager.module.scss';
import { ILeftBlockProps } from './interfaces';

export const LeftBlock: FC<ILeftBlockProps> = ({
  control,
  watch,
  linkButton,
  setLinkButton,
  setValue,
}) => {
  const { t, lang } = useContext(LocalizationContext);
  const { theme } = useContext(ThemeContext);
  const today = dayjs();

  const [isInitializing, setIsInitializing] = useState(true);
  const [tinyKey, setTinyKey] = useState(0);
  const editorRef = useRef<TinyEditor | null>(null);
  const [period, setPeriod] = useState<boolean>(false);
  const [startDate, setStartDate] = useState<string | null>(
    today.hour(0).minute(0).second(0).format()
  );

  const [endDate, setEndDate] = useState<string | null>(
    today.add(1, 'week').hour(23).minute(59).second(59).format()
  );

  useEffect(() => {
    setTinyKey((prev) => prev + 1);
  }, [t, theme]);

  const themeProps = useMemo(() => {
    return theme === ETheme.DARK
      ? {
          skin: 'oxide-dark',
          content_css: 'dark',
          content_style: `.mce-content-body[data-mce-placeholder]:not(.mce-visualblocks)::before {
    color: rgba(255, 255, 255, 0.5);
    opacity: 1;
    }
    `,
        }
      : {};
  }, [theme]);

  useEffect(() => {
    if (period) {
      setValue(
        'publication_period',
        {
          from: formatDateForServer(dayjs(startDate)),
          until: formatDateForServer(dayjs(endDate)),
        },
        { shouldDirty: true }
      );
    } else {
      setValue('publication_period', null, { shouldDirty: true });
    }
  }, [period, startDate, endDate, setValue]);

  const linkButtonToggle = watch('link');
  const periodToggle = watch('publication_period');

  useEffect(() => {
    if (
      linkButtonToggle !== null &&
      linkButtonToggle.label !== null &&
      linkButtonToggle.label !== ''
    ) {
      setLinkButton(true);
    }
  }, [linkButtonToggle]);

  useEffect(() => {
    if (periodToggle !== null && periodToggle.until !== null) {
      setPeriod(true);
      setStartDate(periodToggle.from);
      setEndDate(periodToggle.until);
    }
  }, [periodToggle]);

  const handleToggleLinkButton = (): void => {
    const newState = !linkButton;
    setLinkButton(newState);

    if (!newState) {
      setValue('link.label', '', { shouldDirty: true });
      setValue('link.url', '', { shouldDirty: true });
    }
  };

  return (
    <div className={s.left}>
      <div className={s.block}>
        <H3>{t('news.manager.name.title')}</H3>
        <Controller
          render={({ field: { value, onChange }, fieldState: { error } }) => (
            <Input
              value={value ?? ''}
              onChange={(value) => {
                if (value?.length < 41) onChange(value);
              }}
              variant="secondary"
              error={error?.message}
              placeholder={t('title.insert')}
              bordered
              maxLength={40}
            />
          )}
          name="title"
          control={control}
          rules={{
            required: t('errors.required'),
            minLength: {
              value: 2,
              message: t('errors.minLength', 2),
            },
            maxLength: {
              value: 40,
              message: t('errors.maxLength', 40),
            },
          }}
        />
      </div>

      <div className={s.block}>
        <div className={s.text_block}>
          <H3>{t('news.text')}</H3>
          <span className={s.text}>{t('news.description')}</span>
        </div>

        <Controller
          name="content"
          control={control}
          render={({ field, fieldState: { error } }) => (
            <div className={s.editorWrapper}>
              <Preloader loading={isInitializing} />
              <ReactEditor
                key={tinyKey}
                onInit={(evt, editor) => {
                  setIsInitializing(false);
                  editorRef.current = editor;
                }}
                init={{
                  height: 314,
                  menubar: false,
                  resize: false,
                  plugins: ['lists'],
                  placeholder: t('news.text.placeholder'),
                  block_formats: `${t('left')}=h3;${t('length')}=p`,
                  toolbar: 'blocks | bold italic forecolor | bullist numlist',
                  invalid_elements:
                    'script,iframe,object,embed,form,input,textarea',
                  cleanup_on_startup: true,
                  xss_sanitization: true,
                  convert_urls: false,
                  ...themeProps,
                }}
                onEditorChange={(content) => field.onChange(content)}
                value={field.value}
              />
              {error && <div className={s.errorMessage}>{error.message}</div>}
            </div>
          )}
          rules={{
            required: t('errors.required'),
            minLength: {
              value: 2,
              message: t('errors.minLength', 2),
            },
            maxLength: {
              value: 2048,
              message: t('errors.maxLength', 2048),
            },
          }}
        />
      </div>

      <div className={s.block}>
        <Toggle
          value={linkButton}
          onChange={handleToggleLinkButton}
          label={t('news.manager.button')}
          variant="secondary"
        />
        <Controller
          render={({ field: { value, onChange }, fieldState: { error } }) => (
            <Input
              value={value ?? ''}
              onChange={(value) => {
                if (value?.length < 29) onChange(value);
              }}
              variant="secondary"
              error={error?.message}
              placeholder={t('more')}
              bordered
              maxLength={28}
              disabled={!linkButton}
            />
          )}
          name="link.label"
          control={control}
          rules={{
            required: linkButton ? t('errors.required') : undefined,

            minLength: {
              value: 2,
              message: t('errors.minLength', 2),
            },
            maxLength: {
              value: 28,
              message: t('errors.maxLength', 28),
            },
          }}
        />
        <Controller
          render={({ field, fieldState: { error } }) => (
            <Input
              {...field}
              variant="secondary"
              error={error?.message}
              placeholder={t('news.manager.button.link')}
              bordered
              disabled={!linkButton}
            />
          )}
          name="link.url"
          control={control}
          rules={{
            required: linkButton ? t('errors.required') : undefined,
            minLength: {
              value: 2,
              message: t('errors.minLength', 2),
            },
            maxLength: {
              value: 2048,
              message: t('errors.maxLength', 2048),
            },
          }}
        />
      </div>

      <div className={s.block}>
        <Controller
          render={({ field }) => (
            <Toggle
              value={field.value}
              onChange={field.onChange}
              label={t('published.status')}
              className={s.toggle}
              variant="secondary"
            />
          )}
          name="active"
          control={control}
        />

        <Separator />
        <Toggle
          value={period}
          onChange={() => setPeriod(!period)}
          label={t('period.action')}
          className={s.toggle}
          variant="secondary"
        />

        {period && (
          <div className={s.date}>
            <DateRangePicker
              startDate={startDate}
              setStartDate={setStartDate}
              endDate={endDate}
              setEndDate={setEndDate}
              lastValidDate={dayjs().add(1, 'year').format()}
              firstValidDate={dayjs(today).format()}
              lang={lang}
            />
          </div>
        )}
      </div>
    </div>
  );
};
